/**
 * Cache Management Utilities for Consistent Cross-Device Styling
 * Addresses the issue of different styling on different computers
 */

import logger from './logger';

class CacheManager {
  constructor() {
    this.cachePrefix = 'pebdeq_';
    this.version = this.getAppVersion();
  }

  /**
   * Get current app version from package.json or build timestamp
   */
  getAppVersion() {
    // In production, this should come from a build-time constant
    return process.env.REACT_APP_VERSION || Date.now().toString();
  }

  /**
   * Clear all application caches
   */
  async clearAllCaches() {
    try {
      // Clear browser cache
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(name => caches.delete(name))
        );
      }

      // Clear localStorage related to themes and styles
      this.clearLocalStorageCache();
      
      // Clear sessionStorage
      this.clearSessionStorageCache();

      logger.log('✅ All caches cleared successfully');
      return true;
    } catch (error) {
      logger.error('❌ Error clearing caches:', error);
      return false;
    }
  }

  /**
   * Clear localStorage cache
   */
  clearLocalStorageCache() {
    const keysToRemove = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.startsWith(this.cachePrefix) ||
        key.includes('theme') ||
        key.includes('style') ||
        key.includes('css')
      )) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach(key => localStorage.removeItem(key));
    logger.log(`Cleared ${keysToRemove.length} localStorage items`);
  }

  /**
   * Clear sessionStorage cache
   */
  clearSessionStorageCache() {
    const keysToRemove = [];
    
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && (
        key.startsWith(this.cachePrefix) ||
        key.includes('theme') ||
        key.includes('style') ||
        key.includes('css')
      )) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach(key => sessionStorage.removeItem(key));
    logger.log(`Cleared ${keysToRemove.length} sessionStorage items`);
  }

  /**
   * Force reload all CSS files
   */
  async reloadAllCSS() {
    try {
      const cssLinks = document.querySelectorAll('link[rel="stylesheet"]');
      const promises = [];

      cssLinks.forEach(link => {
        const promise = new Promise((resolve) => {
          const newLink = document.createElement('link');
          newLink.rel = 'stylesheet';
          newLink.type = 'text/css';
          
          // Add cache-busting parameter
          const url = new URL(link.href);
          url.searchParams.set('v', this.version);
          newLink.href = url.toString();
          
          newLink.onload = () => {
            // Remove old link after new one loads
            if (link.parentNode) {
              link.parentNode.removeChild(link);
            }
            resolve();
          };
          
          newLink.onerror = () => {
            logger.warn('Failed to reload CSS:', url.toString());
            resolve(); // Continue even if one fails
          };
          
          // Insert new link before the old one
          link.parentNode.insertBefore(newLink, link);
        });
        
        promises.push(promise);
      });

      await Promise.all(promises);
      logger.log('✅ All CSS files reloaded');
      return true;
    } catch (error) {
      logger.error('❌ Error reloading CSS:', error);
      return false;
    }
  }

  /**
   * Check if cache should be cleared based on version
   */
  shouldClearCache() {
    const storedVersion = localStorage.getItem(`${this.cachePrefix}version`);
    return storedVersion !== this.version;
  }

  /**
   * Update stored version after cache operations
   */
  updateStoredVersion() {
    localStorage.setItem(`${this.cachePrefix}version`, this.version);
  }

  /**
   * Initialize cache management on app start
   */
  async initialize() {
    if (this.shouldClearCache()) {
      logger.log('Version mismatch detected. Clearing caches...');
      await this.clearAllCaches();
      await this.reloadAllCSS();
      this.updateStoredVersion();
      
      // Show user notification
      this.showCacheUpdateNotification();
    }
  }

  /**
   * Show notification to user about cache update
   */
  showCacheUpdateNotification() {
    logger.log('🔄 Cache updated for consistent styling across devices');
  }

  /**
   * Force refresh for different computers showing different styles
   */
  async hardRefresh() {
    await this.clearAllCaches();
    await this.reloadAllCSS();
    
    // Force page reload after a short delay
    setTimeout(() => {
      window.location.reload(true);
    }, 1000);
  }

  /**
   * Add cache-busting parameters to URLs
   */
  addCacheBuster(url) {
    const urlObj = new URL(url, window.location.origin);
    urlObj.searchParams.set('v', this.version);
    urlObj.searchParams.set('cb', Date.now());
    return urlObj.toString();
  }

  /**
   * Preload critical CSS with cache-busting
   */
  async preloadCriticalCSS() {
    const criticalCSS = [
      '/static/css/main.css',
      '/themes/pq-light.css'
    ];

    const promises = criticalCSS.map(cssPath => {
      return new Promise((resolve) => {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'style';
        link.href = this.addCacheBuster(cssPath);
        link.onload = resolve;
        link.onerror = resolve;
        document.head.appendChild(link);
      });
    });

    await Promise.all(promises);
    logger.log('✅ Critical CSS preloaded');
  }

  /**
   * Monitor for style inconsistencies
   */
  monitorStyleConsistency() {
    // Check if computed styles match expected values
    const testElement = document.createElement('div');
    testElement.className = 'cache-test-element';
    testElement.style.cssText = `
      position: absolute;
      top: -9999px;
      left: -9999px;
      width: 100px;
      height: 100px;
      background: var(--primary-color, #007bff);
    `;
    
    document.body.appendChild(testElement);
    
    const computedStyle = window.getComputedStyle(testElement);
    const backgroundColor = computedStyle.backgroundColor;
    
    document.body.removeChild(testElement);
    
    // Store this as baseline for comparison
    const expectedColor = localStorage.getItem(`${this.cachePrefix}expected_primary_color`);
    if (!expectedColor) {
      localStorage.setItem(`${this.cachePrefix}expected_primary_color`, backgroundColor);
    } else if (expectedColor !== backgroundColor && backgroundColor !== 'rgba(0, 0, 0, 0)') {
      logger.warn('Style inconsistency detected. May need cache refresh.');
      return false;
    }
    
    return true;
  }
}

// Create singleton instance
const cacheManager = new CacheManager();

export default cacheManager;

// Auto-initialize disabled - call manually if needed
// cacheManager.initialize();
